import { DOCUMENT, DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Observable, filter, fromEvent, map, merge, throttleTime } from 'rxjs';

import { STORAGE_KEY } from '../constants/storage.constants';
import { LocalStorageService } from './local-storage.service';

const ACTIVITY_THROTTLE_MS = 15 * 1000;
const ACTIVITY_EVENTS: readonly string[] = ['mousemove', 'keydown', 'click', 'touchstart', 'wheel'];

@Injectable({ providedIn: 'root' })
export class ActivityTrackerService {
  public readonly otherTabActivity$: Observable<void>;

  private readonly document: Document = inject(DOCUMENT);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly storage: LocalStorageService = inject(LocalStorageService);
  private lastSeenAt: number = Date.now();
  private paused = false;

  constructor() {
    this.otherTabActivity$ = fromEvent<StorageEvent>(window, 'storage').pipe(
      filter((event) => event.key === STORAGE_KEY.lastActivityAt),
      map(() => undefined),
    );

    merge(...ACTIVITY_EVENTS.map((name) => fromEvent(this.document, name)))
      .pipe(
        filter(() => !this.paused),
        throttleTime(ACTIVITY_THROTTLE_MS),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => this.record(),
        error: () => this.record(),
      });
  }

  public record(): void {
    this.lastSeenAt = Date.now();
    this.storage.setItem<number>(STORAGE_KEY.lastActivityAt, this.lastSeenAt);
  }

  public lastActivityAt(): number {
    const shared = this.storage.getItem<number>(STORAGE_KEY.lastActivityAt) ?? 0;

    return Math.max(shared, this.lastSeenAt);
  }

  public pause(paused: boolean): void {
    this.paused = paused;
  }
}
